import React, { useState } from "react";
import { getChainStatusChart } from "../../Requests/optionGreek";
import { packetErrorHandler } from "../../Utils/requestUtils";
import { Menu, MenuItem, Tooltip } from "@mui/material";
import Cell from "../TableTemplate/Cell";
import { IExtraTypeUIProps } from "../TableTemplate/tableTemplate";
import { IOptionsGreekTableReport } from "../../Interfaces/optionGreek";
import { Graph_Dto } from "../../Interfaces/orderbookGraphInterfaces";
import Modal from "../../Components/Modal/Modal";
import { WrappedTimeSeriesChart } from "../../Components/Timeseries/WrappedTimeSeriesChart";
import FullScreenLoading from "../../Components/Loading/FullScreenLoading";
import DatePicker from "../../Components/DatePicker";
import Button from "../../Components/Button";

/**
 * Right-click chart cell dedicated to the "وضعیت زنجیره" (ChainStatus) column. Kept separate from
 * WithChartCell/TimeseriesChart on purpose -- those are shared by every other WithChart column in
 * this table (and elsewhere), and shouldn't change behavior for them.
 */
const ChainStatusChartCell = ({
  cellType,
  headerName,
  row,
  translateHeaderName,
}: IExtraTypeUIProps<IOptionsGreekTableReport>) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [graphData, setGraphData] = useState<Graph_Dto[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [graphDate, setGraphDate] = useState<{
    fromDate?: string;
    toDate?: string;
  }>({});

  const getChartHandler = () => {
    setLoading(true);
    setIsModalOpen(false);
    getChainStatusChart(
      headerName,
      row.callInstrumentCode,
      row.callInstrument,
      graphDate.fromDate,
      graphDate.toDate,
    )
      .then((resp) => {
        setGraphData(resp);
        handleCloseMenu();
        setIsModalOpen(true);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const rightClickHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(true);
    setAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    setAnchorEl(null);
    setIsTooltipOpen(false);
  };

  const graphDateChangeHandler = (name: string, value: string) => {
    setGraphDate((previous) => ({ ...previous, [name]: value }));
  };

  const getModalUI = () => (
    <div
      className="h-full w-full min-h-0 flex flex-col justify-start items-stretch gap-3"
      dir="ltr"
    >
      <div className="text-lg font-bold text-right shrink-0" dir="rtl">
        {translateHeaderName || headerName} — {row.baseInstrument} —{" "}
        {row.dueDate}
      </div>
      <div className="w-full min-h-0 flex-1 flex flex-row gap-4 items-stretch">
        <div className="flex-1 min-w-0 min-h-0 h-full">
          <WrappedTimeSeriesChart
            dataset={graphData}
            YValueType="number"
            showFullDateTime
            className="w-full h-full"
          />
        </div>
        <div
          className="w-52 shrink-0 flex flex-col justify-start items-stretch gap-3"
          dir="rtl"
        >
          <DatePicker
            label="از تاریخ"
            name="fromDate"
            onChange={graphDateChangeHandler}
            value={graphDate.fromDate}
          />
          <DatePicker
            label="تا تاریخ"
            name="toDate"
            onChange={graphDateChangeHandler}
            value={graphDate.toDate}
          />
          <Button text="جستجو" onClick={getChartHandler} variant="contained" />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {loading ? <FullScreenLoading /> : <></>}
      {isModalOpen ? (
        <Modal
          onClose={() => {
            handleCloseMenu();
            setIsModalOpen(false);
          }}
          fullScreen
          title={[
            translateHeaderName || headerName,
            row.baseInstrument,
            row.dueDate,
          ]
            .filter(Boolean)
            .join(" — ")}
          UI={getModalUI()}
          modalBoxClassName="!z-[9999]"
        />
      ) : (
        <></>
      )}
      <Tooltip
        title="نمودار"
        className="!w-full !relative"
        open={isTooltipOpen}
        onMouseEnter={() => setIsTooltipOpen(true)}
        onMouseLeave={() => setIsTooltipOpen(false)}
      >
        <div onContextMenu={rightClickHandler} className="ltr">
          <Cell clonedRow={row} headerName={headerName} type={cellType} />
          {isMenuOpen ? (
            <Menu
              className="!z-[9998]"
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleCloseMenu}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem onClick={getChartHandler}>نمودار</MenuItem>
            </Menu>
          ) : (
            <></>
          )}
        </div>
      </Tooltip>
    </>
  );
};

export default ChainStatusChartCell;
